import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MatchGamePage.css';
import poko_stand from '../assets/poko_handsInWaist.png';
import logo from '../assets/signmates_logo_home.svg';

// Imports all common sign images and returns an object mapping filenames (without extension) to their image modules
function importAllSigns() {
	const context = require.context('../assets/signs/commonSign', false, /\.(png|jpe?g|svg)$/);
	const signs = {};
	context.keys().forEach((key) => {
		const fileName = key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '');
		signs[fileName] = context(key);
	});
	return signs;
}

// Returns a new array with elements shuffled in random order
function shuffleArray(array) {
	return [...array].sort(() => Math.random() - 0.5);
}

function MatchGamePage() {
	const navigate = useNavigate();
	const allSigns = importAllSigns();
	const [learntSigns, setLearntSigns] = useState([]);
	const [signMode, setSignMode] = useState('all');
	const [signs, setSigns] = useState([]);
	const [firstVisit, setFirstVisit] = useState(true);
	const [isGameStarted, setIsGameStarted] = useState(false);
	const [showInstructions, setShowInstructions] = useState(false);
	const [selectedCommon, setSelectedCommon] = useState(null);
	const [selectedSign, setSelectedSign] = useState(null);
	const [shuffledCommonKeys, setShuffledCommonKeys] = useState([]);
	const [shuffledSignEntries, setShuffledSignEntries] = useState([]);
	const [showAllCards, setShowAllCards] = useState(false);
	const [alwaysShowCards, setAlwaysShowCards] = useState(false);
	const [countdown, setCountdown] = useState(null);
	const [matched, setMatched] = useState([]);
	const [moves, setMoves] = useState(0);

	// Resets the game state, shows all cards briefly if needed, and restarts the game
	const resetGame = () => {
		setSelectedCommon(null);
		setSelectedSign(null);
		setMatched([]);
		setMoves(0);
		setShowAllCards(!alwaysShowCards); // Show cards briefly if not always open
		if (!alwaysShowCards) setCountdown(5); // Start countdown only if cards aren't always open
		handleStartGame();
	};

	// Initializes the game by selecting signs (learnt or all), shuffling them, and setting up the board
	const handleStartGame = () => {
		const selectedSignSet =
			signMode === 'learnt'
				? Object.fromEntries(
					learntSigns.map(key => [key, allSigns[key]]).filter(([_, val]) => val)
				)
				: allSigns;

		const limitedKeys = shuffleArray(Object.keys(selectedSignSet)).slice(0, 4);
		const limitedSigns = {};
		limitedKeys.forEach(key => {
			limitedSigns[key] = selectedSignSet[key];
		});

		setSigns(limitedSigns);
		setShuffledCommonKeys(shuffleArray(limitedKeys));
		setShuffledSignEntries(shuffleArray(Object.entries(limitedSigns)));
		setIsGameStarted(true);
		if (firstVisit)
			setShowInstructions(true);
	};

	// Starts countdown after user confirms they understand how the game works
	const handleGotItAndStartCountdown = () => {
		setShowInstructions(false);
		setFirstVisit(false);
		setShowAllCards(true);
		setCountdown(5); // Countdown starts only after user clicks "Got it"
	};

	// Handles logic when a card is clicked, tracking selections while avoiding repeated clicks
	const handleCardClick = (type, key) => {
		if (matched.includes(key)) return;

		if (type === 'common') {
			if (selectedCommon === key) return;
			setSelectedCommon(key);
		} else {
			if (selectedSign === key) return;
			setSelectedSign(key);
		}
	};

	// Modal that shows instructions for how to play the game (shown only on first visit or when triggered)
	const InstructionModal = ({ handleGotItAndStartCountdown }) => {
		if (!showInstructions) return null;

		const justClose = () => {
			setShowInstructions(false);
		};

		return (
			<div className="instructions-modal-overlay" onClick={justClose}>
				<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
					{!firstVisit && (
						<button className="close-button" onClick={justClose}>×</button>
					)}
					<h2>🧠 How to Play?</h2>
					<div className="instruction-section">
						<h3>1. How to Play</h3>
						<p>• 👀 Look at all the cards for 5 seconds</p>
						<p>• 🖱️ Tap one card from each side to find a match</p>
						<p>• 🎯 Match them all with the fewest moves!</p>
						<p>• 🪄 Need help? Turn on <strong>“Play with all cards open”</strong></p>
					</div>

					{firstVisit ? (
						<button className="game-btn" onClick={handleGotItAndStartCountdown}>
							Got it, let’s go!
						</button>
					) : (
						<button className="game-btn" onClick={justClose}>
							Close
						</button>
					)}
				</div>
			</div>
		);
	};

	// Modal that appears when the user successfully matches all signs
	const WinGameModal = () => {
		if (matched.length !== Object.keys(signs).length) return null;

		return (
			<div className="instructions-modal-overlay">
				<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
					<div className='page-title'>
						<h2>🎉 You did it!</h2>
						<h6>You matched all the signs in {moves} moves.</h6>
					</div>
					<h5 className="review-title">🧐 Let’s review what you matched:</h5>
					<div className="cards">
						{Object.entries(signs).map(([key, img]) => (
							<div key={`review-${key}`} className="card flipped">
								<div className="card-inner">
									<div className="card-back">
										<img src={img} alt={key} />
									</div>

								</div>
								<p className="review-key-label">{key.replace(/_/g, ' ')}</p>
							</div>
						))}
					</div>
					<button onClick={() => navigate('/learn')} className="game-btn revisit-btn">🧠 Revisit in Learn Page</button>
					<div className='row-wrapper'>
						<button onClick={resetGame} className="game-btn">🔁 Play Again</button>
						<button onClick={() => navigate('/games')} className="game-btn">🎮 Try Another Game</button>
					</div>
				</div>
			</div>
		);
	};

	// Loads learnt signs from localStorage when the game component mounts
	useEffect(() => {
		const stored = localStorage.getItem('learntSigns');
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				setLearntSigns(parsed.common || []);
			} catch (err) {
				console.error('Failed to parse learnt signs from localStorage', err);
			}
		}
	}, []);

	// Compares selected cards and updates matched list if they match; resets selection after a short delay
	useEffect(() => {
		if (selectedCommon && selectedSign) {
			setMoves(prev => prev + 1);
			if (selectedCommon === selectedSign) {
				setMatched(prev => [...prev, selectedCommon]);
			}
			setTimeout(() => {
				setSelectedCommon(null);
				setSelectedSign(null);
			}, 800);
		}
	}, [selectedCommon, selectedSign]);

	// Handles countdown logic to hide cards after a short preview duration
	useEffect(() => {
		if (countdown !== null && countdown > 0) {
			const timer = setTimeout(() => {
				setCountdown(countdown - 1);
			}, 1000);
			return () => clearTimeout(timer);
		} else if (countdown === 0) {
			setShowAllCards(false);
			setCountdown(null); // clear countdown
		}
	}, [countdown]);


	return (
		<div className="match-game-page page">
			<div className="white-wrapper container">
				{!isGameStarted ? (
					<>
						<div className="game-page-title">
							<button className='game-back-btn' onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
							<h1>🃏 Memory Match</h1>
						</div>
						<div className="speech-section">
							<img src={poko_stand} alt="Buddy standing" className="poko" />
							<div className="speech-bubble-right">
								<div className="game-instruction">
									🧠 Ready to match signs? Choose your sign set and start the game!
								</div>
								<div className="game-sign-option">
									<p>Choose your sign set:</p>
									<div className="radio-group">
										<label>
											<input
												type="radio"
												name="signMode"
												value="all"
												checked={signMode === 'all'}
												onChange={() => setSignMode('all')}
											/>
											All signs
											<div className="option-description">
												🌀 Play with all the signs, even the ones you haven’t learned yet!
											</div>
										</label>
										<label className={learntSigns.length < 4 ? 'disabled' : ''}>
											<input
												type="radio"
												name="signMode"
												value="learnt"
												disabled={learntSigns.length < 4}
												checked={signMode === 'learnt'}
												onChange={() => setSignMode('learnt')}
											/>
											Learnt signs

											{learntSigns.length < 4 ? (
												<div className="option-description disabled">
													🧠 You’ve learnt <strong>{learntSigns.length} common signs</strong> so far! <br />
													📖 Keep learning — unlock this mode after learning at least <strong>4 common signs</strong>.<br />
													👉 <button onClick={() => navigate('/learn')}>Go to Learn</button>
												</div>
											) : (
												<div className="option-description">
													✅ You’ve learnt <strong>{learntSigns.length} signs</strong>! <br />
													🥳 Pick this mode to practice signs you already know. <br />
													👉 <button onClick={() => navigate('/learn')}>Want to learn more?</button>
												</div>
											)}
										</label>
									</div>
								</div>
								<button className='game-start-button' onClick={handleStartGame}>Start!</button>
							</div>
						</div>
					</>
				) : (
					<div className="match-game-section">
						<div className="game-page-title">
							<button className='game-back-btn' onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
							<h1>🃏 Memory Match</h1>
						</div>

						<div className="tip">Tap a card on each side to match them!</div>

						<div className="scoreboard">
							<div>Matches: <strong>{matched.length} / {Object.keys(signs).length}</strong></div>
							<div>Moves: <strong>{moves}</strong></div>
							<div className="toggle-wrapper">
								<label className="switch">
									<input
										type="checkbox"
										checked={alwaysShowCards}
										onChange={() => setAlwaysShowCards(!alwaysShowCards)}
									/>
									<span className="slider round"></span>
								</label>
								<span className="toggle-label">Play with all cards open</span>
							</div>
							<button className="help-button" onClick={() => setShowInstructions(true)}>
								❔
							</button>
							<button
								onClick={resetGame}
								className={`restart-btn ${moves === 0 ? 'disabled' : ''}`}
								disabled={moves === 0}
							>
								Restart
							</button>
						</div>
						<div className='countdown-wrapper'>
							{showAllCards && countdown !== null && (
								<div className="countdown-message">
									🧠 Remember them in {countdown}...
								</div>
							)}
						</div>

						<div className="sets-container">
							<div className="card-set">
								<h3>Common Words</h3>
								<div className="cards">
									{shuffledCommonKeys.map((key) => (
										<div
											key={`common-${key}`}
											className={`card 
												${(alwaysShowCards || showAllCards || selectedCommon === key || matched.includes(key)) ? 'flipped' : ''} 
												${matched.includes(key) ? 'matched' : ''} 
												${alwaysShowCards && selectedCommon === key ? 'selected' : ''}`}
											onClick={() => handleCardClick('common', key)}
										>
											<div className="card-inner">
												<div className="card-front">
													<img src={logo} alt='logo' />
												</div>
												<div className="card-back letter">{key.replace(/_/g, ' ')}</div>
											</div>
										</div>
									))}
								</div>
							</div>

							<div className="card-set">
								<h3>Sign Images</h3>
								<div className="cards">
									{shuffledSignEntries.map(([key, img]) => (
										<div
											key={`sign-${key}`}
											className={`card 
												${(alwaysShowCards || showAllCards || selectedSign === key || matched.includes(key)) ? 'flipped' : ''} 
												${matched.includes(key) ? 'matched' : ''} 
												${alwaysShowCards && selectedSign === key ? 'selected' : ''}`}
											onClick={() => handleCardClick('sign', key)}
										>
											<div className="card-inner">
												<div className="card-front">
													<img src={logo} alt='logo' />
												</div>
												<div className="card-back">
													<img src={img} alt={key} />
												</div>
											</div>
										</div>
									))}
								</div>
							</div>

						</div>

						{showInstructions && (
							<InstructionModal handleGotItAndStartCountdown={handleGotItAndStartCountdown} />
						)}

						{WinGameModal()}


					</div>
				)}
			</div>
		</div>
	);
}

export default MatchGamePage;
